import { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import { InteractionStatus } from "@azure/msal-browser";
import { useAuth } from "../../context/AuthContext";
import { RouterPathEnum } from "../../constants/router";
import { SignInButton } from "./SignInButton";
import LoginFooter from "./LoginFooter";
import { LoginHeader } from "./LoginHeader";
import LeftSideImageText from "./LeftSideImageText";
import { COMMON } from "../../constants/common";
import { postDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateAccessToken } = useAuth();
  const { instance, inProgress, accounts } = useMsal();
  const hasCheckedUser = useRef(false);

  const [chatBodyHeight, setChatBodyHeight] = useState<number>();
  const [isCheckingUser, setIsCheckingUser] = useState<boolean>(false);
  const [isProcessingAuth, setIsProcessingAuth] = useState<boolean>(false);

  const handleSetChatBodyHeight = () => {
    const mainSectionHeight =
      document.getElementById("loginMainContainer")?.offsetHeight;
    const chatTextareaContainer = document.getElementById(
      "loginFooterContainer"
    )?.offsetHeight;

    if (mainSectionHeight && chatTextareaContainer) {
      const chatBodyHeight = mainSectionHeight - chatTextareaContainer;
      setChatBodyHeight(chatBodyHeight);
    }
  };
  useEffect(() => {
    //NOTE:: Function to handle setting chat body height
    const updateChatBodyHeight = () => {
      handleSetChatBodyHeight();
    };

    //NOTE:: Call it once on mount
    updateChatBodyHeight();

    //NOTE:: Add event listener for window resize
    window.addEventListener("resize", updateChatBodyHeight);

    //NOTE:: Cleanup function to remove the event listener on unmount
    return () => {
      window.removeEventListener("resize", updateChatBodyHeight);
    };
  }, []);

  useEffect(() => {
    // Only run on login page to prevent loops
    const currentPath = location.pathname;
    if (
      !currentPath.includes(RouterPathEnum.LOGIN) ||
      currentPath.includes(RouterPathEnum.USER_REQUEST) ||
      currentPath.includes(RouterPathEnum.USER_INACTIVE) ||
      hasCheckedUser.current
    ) {
      return;
    }

    const getToken = async () => {
      if (inProgress === InteractionStatus.None && accounts.length > 0 && !hasCheckedUser.current) {
        hasCheckedUser.current = true;
        setIsProcessingAuth(true);
        setIsCheckingUser(true);
        try {
          // Show loader while acquiring token
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          localStorage.setItem(COMMON.ACCESS_TOKEN, response.accessToken);
          updateAccessToken(response.accessToken);

          // Check if user exists in database before redirecting to dashboard
          const url = `${RestfulUrls.BASE_URL}${RestfulUrls.GET_USER_DETAILS}`;
          postDataFromApi<any>(
            url,
            null,
            () => {
              // User exists, redirect to dashboard
              setIsCheckingUser(false);
              setIsProcessingAuth(false);
              navigate(`/${RouterPathEnum.DGO}/${RouterPathEnum.DASHBOARD}`);
            },
            { isAuthRequired: true },
            navigate,
            (error: any) => {
              // Handle user existence errors
              setIsCheckingUser(false);
              setIsProcessingAuth(false);
              const errorDetail = error?.response?.data?.detail || error?.detail || "";

              if (errorDetail === "UserDoesNotExists") {
                // User doesn't exist, redirect to user request page
                navigate(`/${RouterPathEnum.USER_REQUEST}`);
              } else if (errorDetail === "UserInactive") {
                // User is inactive, redirect to user inactive page
                navigate(`/${RouterPathEnum.USER_INACTIVE}`);
              } else {
                // Other errors - still redirect to dashboard and let it handle the error
                navigate(`/${RouterPathEnum.DGO}/${RouterPathEnum.DASHBOARD}`);
              }
            }
          );

        } catch (error) {
          console.error("Token acquisition failed:", error);
          setIsCheckingUser(false);
          setIsProcessingAuth(false);
        }
      }
    };

    getToken();
  }, [inProgress, accounts, instance, navigate, updateAccessToken, location.pathname]);
  
  // Show loader only when SSO interaction is actively in progress or when checking user
  useEffect(() => {
    const currentPath = location.pathname;
    // Only show loader on login page
    if (currentPath.includes(RouterPathEnum.LOGIN) && 
        !currentPath.includes(RouterPathEnum.USER_REQUEST) &&
        !currentPath.includes(RouterPathEnum.USER_INACTIVE)) {
      // Show loader when:
      // 1. SSO interaction is actively in progress (user clicked login and redirecting)
      // 2. OR we're actively processing authentication (checking user)
      if (inProgress !== InteractionStatus.None || isProcessingAuth) {
        setIsCheckingUser(true);
      } else {
        // No active process - hide loader to allow user to click login button
        setIsCheckingUser(false);
      }
    }
  }, [location.pathname, inProgress, isProcessingAuth]);
  
  // Reset loader when navigating away from login page
  useEffect(() => {
    const currentPath = location.pathname;
    if (!currentPath.includes(RouterPathEnum.LOGIN)) {
      setIsCheckingUser(false);
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        position: "relative",
      }}
      id="loginMainContainer"
    >
      <LoginHeader />
      <Box
        className="login-main-container"
        style={{ height: `${chatBodyHeight}px` }}
      >
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              <LeftSideImageText />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                py: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  p: 2,
                }}
              >
                <SignInButton />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box id="loginFooterContainer">
        <LoginFooter />
      </Box>
      
      {/* Loading overlay */}
      {isCheckingUser && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontWeight: 500,
            }}
          >
            Checking user details, please wait...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Login;
